import type {
  Customer,
  CustomerAccessTokenCreatePayload,
  CustomerAddressCreatePayload,
  CustomerAddressDeletePayload,
  CustomerAddressUpdatePayload,
  CustomerCreatePayload,
  CustomerDefaultAddressUpdatePayload,
  CustomerRecoverPayload,
  CustomerUpdatePayload,
  Maybe,
  Metafield,
  MetafieldReferenceConnection,
  MetafieldReferenceEdge,
  Order,
  OrderConnection,
  OrderEdge,
  OrderLineItem,
  OrderLineItemConnection,
  OrderLineItemEdge,
  PageInfo,
  Product,
  ProductVariant,
  ProductVariantConnection,
} from "@shopify/hydrogen-react/storefront-api-types";
import { mutation, query } from "gql-query-builder";
import {
  APP_NAME,
  customerAccessTokenFields,
  customerFields,
  customerUserErrorFields,
  fulfillmentFields,
  mailingAddressFields,
  moneyFields,
  orderAmountFields,
  orderFields,
  pageFields,
  productFields,
  userErrorFields,
  variantFields,
} from "./fields";
import {
  birthMetafield,
  cardInfoMetafield,
  excludeLimitMetafield,
  genderMetafield,
  heightMetafield,
  weightMetafield,
  wishListMetafield,
} from "./metafields/customer-metafields";
import {
  brandStoreOrderIdMetafield,
  drawStatusMetafield,
  featuredImageMetafield,
  giftingIsAcceptedMetafield,
  giftingMessageTemplateMetafield,
  giftingTokenMetafield,
  instagramMetafield,
  isFamilySaleMetafield,
  isOfflineMetafield,
  isRaffleOrderMetafield,
  productCodeMetafield,
  raffleBrandPayMethodIdMetafield,
  raffleCardInfoMetafield,
  raffleCustomerNameMetafield,
  raffleInstagramMetafield,
  raffleIsOfflinePickupMetafield,
  rafflePhoneMetafield,
  raffleShippingMethodMetafield,
  raffleStatusMetafield,
  returnStatusMetafield,
  scoreMetafield,
  serialNumberMetafield,
} from "./metafields/order-metafields";
import type { IProductMetafields } from "./product-gql";
import {
  automaticDiscountApplicationFragment,
  discountCodeApplicationFragment,
  manualDiscountApplicationFragment,
  moneyFragment,
  percentageFragment,
} from "./fragments";
import {
  dropDateMetafield,
  launchMetafield,
  releaseDateMetafield,
  thumbnailImagesMetafield,
} from "./metafields/product-metafields";
import type IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";

export const addresses = {
  operation: "addresses(first: 250)",
  fields: [{ edges: [{ node: mailingAddressFields }] }],
  variables: {},
};

export const lineItems = {
  operation: "lineItems(first: 100)",
  fields: [
    {
      edges: [
        {
          node: [
            {
              variant: [
                {
                  operation: "ProductVariant",
                  fields: [
                    ...variantFields,
                    {
                      product: [
                        ...productFields,
                        dropDateMetafield,
                        launchMetafield,
                        thumbnailImagesMetafield,
                      ],
                    },
                  ],
                  fragment: true,
                },
              ],
              discountAllocations: [
                {
                  allocatedAmount: moneyFields,
                  discountApplication: [
                    manualDiscountApplicationFragment,
                    automaticDiscountApplicationFragment,
                    discountCodeApplicationFragment,
                  ],
                },
              ],
            },
            "title",
            "quantity",
            "currentQuantity",
            { discountedTotalPrice: moneyFields },
            { originalTotalPrice: moneyFields },
          ],
        },
      ],
    },
  ],
  variables: {},
};

export const discountApplications = {
  operation: "discountApplications(first: 10)",
  fields: [
    {
      edges: [
        {
          node: [
            {
              "... on ManualDiscountApplication": ["title", "description"],
              "... on AutomaticDiscountApplication": ["title"],
              "... on DiscountCodeApplication": ["code", "applicable"],
              value: [moneyFragment, percentageFragment],
            },
          ],
        },
      ],
    },
  ],
  variables: {},
};

export const successfulFulfillments = {
  operation: "successfulFulfillments(first: 100)",
  fields: [
    ...fulfillmentFields,
    {
      operation: "fulfillmentLineItems(first: 100)",
      fields: [
        {
          edges: [
            {
              node: [
                {
                  lineItem: [
                    "title",
                    { variant: ["id", { product: ["vendor", "id"] }] },
                  ],
                },
              ],
            },
          ],
        },
      ],
      variables: {},
    },
  ],
  variables: {},
};

export const orderMetafields: Record<string, IQueryBuilderOptions[]> = {
  tune: [],
  yeseyesee: [],
  thisisneverthat: [],
  khakis: [
    giftingIsAcceptedMetafield,
    giftingMessageTemplateMetafield,
    giftingTokenMetafield,
    isRaffleOrderMetafield,
    raffleIsOfflinePickupMetafield,
    raffleShippingMethodMetafield,
    raffleInstagramMetafield,
    raffleCustomerNameMetafield,
    rafflePhoneMetafield,
    raffleStatusMetafield,
    raffleCardInfoMetafield,
    raffleBrandPayMethodIdMetafield,
  ],
  sunlove: [],
  wellbeingexpress: [],
};

export const orders = {
  operation: "orders(first: 250, sortKey: PROCESSED_AT, reverse: true)",
  fields: [
    {
      edges: [
        {
          node: [
            ...orderFields,
            lineItems,
            successfulFulfillments,
            drawStatusMetafield,
            featuredImageMetafield,
            isOfflineMetafield,
            returnStatusMetafield,
            instagramMetafield,
            serialNumberMetafield,
            scoreMetafield,
            productCodeMetafield,
            cardInfoMetafield,
            excludeLimitMetafield,
            discountApplications,
            isFamilySaleMetafield,
            brandStoreOrderIdMetafield,
            releaseDateMetafield,
            ...orderMetafields[APP_NAME],
          ],
        },
      ],
    },
  ],
  variables: {},
};

/**
 * 고객 생성 (회원가입)
 */
export interface ICustomerCreateMutation {
  customerCreate: CustomerCreatePayload;
}

export const customerCreateMutation = mutation(
  {
    operation: "customerCreate",
    fields: [
      { customer: customerFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: { input: { type: "CustomerCreateInput!" } },
  },
  undefined,
  { operationName: "CustomerCreate" },
);

/**
 * 고객 토큰 생성 - 로그인 시 호출
 */
export interface ICustomerAccessTokenCreateMutation {
  customerAccessTokenCreate: CustomerAccessTokenCreatePayload;
}

export const customerAccessTokenCreateMutation = mutation(
  {
    operation: "customerAccessTokenCreate",
    fields: [
      { customerAccessToken: customerAccessTokenFields },
      { customerUserErrors: customerUserErrorFields },
    ],
    variables: { input: { type: "CustomerAccessTokenCreateInput!" } },
  },
  undefined,
  { operationName: "CustomerAccessTokenCreate" },
);

/**
 * 비밀번호 재설정 - 이메일 전송
 */
export interface ICustomerRecoverMutation {
  customerRecover: CustomerRecoverPayload;
}

export const customerRecoverMutation = mutation(
  {
    operation: "customerRecover",
    fields: [
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: { email: { type: "String!" } },
  },
  undefined,
  { operationName: "CustomerRecover" },
);

/**
 * 고객 배송지 생성
 */
export interface ICustomerAddressCreateMutation {
  customerAddressCreate: CustomerAddressCreatePayload;
}

export const customerAddressCreateMutation = mutation(
  {
    operation: "customerAddressCreate",
    fields: [
      { customerAddress: mailingAddressFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      address: { type: "MailingAddressInput!" },
      customerAccessToken: { type: "String!" },
    },
  },
  undefined,
  { operationName: "CustomerAddressCreate" },
);

/**
 * 고객 배송지 삭제
 */
export interface ICustomerAddressDeleteMutation {
  customerAddressDelete: CustomerAddressDeletePayload;
}

export const customerAddressDeleteMutation = mutation(
  {
    operation: "customerAddressDelete",
    fields: [
      "deletedCustomerAddressId",
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      customerAccessToken: { type: "String!" },
      id: { type: "ID!" },
    },
  },
  undefined,
  { operationName: "CustomerAddressDelete" },
);

/**
 * 고객 배송지 수정
 */
export interface ICustomerAddressUpdateMutation {
  customerAddressUpdate: CustomerAddressUpdatePayload;
}

export const customerAddressUpdateMutation = mutation(
  {
    operation: "customerAddressUpdate",
    fields: [
      { customerAddress: mailingAddressFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      address: { type: "MailingAddressInput!" },
      customerAccessToken: { type: "String!" },
      id: { type: "ID!" },
    },
  },
  undefined,
  { operationName: "CustomerAddressUpdate" },
);

/**
 * 고객 배송지의 기본배송지 설정
 */
export interface ICustomerDefaultAddressUpdateMutation {
  customerDefaultAddressUpdate: CustomerDefaultAddressUpdatePayload;
}

export const customerDefaultAddressUpdateMutation = mutation(
  {
    operation: "customerDefaultAddressUpdate",
    fields: [
      { customer: customerFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      addressId: { type: "ID!" },
      customerAccessToken: { type: "String!" },
    },
  },
  undefined,
  { operationName: "CustomerDefaultAddressUpdate" },
);

export interface IOrdersLineItemsEdges extends OrderLineItemEdge {
  node: OrderLineItem & {
    returnReason?: string;
    index?: number;
    variant: Maybe<
      ProductVariant & { product: Product & IProductMetafields }
    > | null;
  };
}

/**
 * 로그인시 생성된 고객 토큰으로 고객 정보 조회
 */
export interface IOrdersEdgesNode extends Order {
  drawStatus?: Metafield;
  featuredImage?: Metafield;
  instagram?: Metafield;
  serialNumber?: Metafield;
  score?: Metafield;
  productCode?: Metafield;
  returnStatus?: Metafield;
  isOffline?: Metafield;
  exclude_limit?: Metafield;
  cardInfo?: Metafield;
  isfamilysale?: Metafield;
  brandStoreOrderIds?: Metafield;
  giftingIsAccepted?: Metafield;
  giftingMessageTemplate?: Metafield;
  giftingToken?: Metafield;
  lineItems: OrderLineItemConnection & {
    edges: IOrdersLineItemsEdges[];
  };
}

export interface IOrdersEdges extends OrderConnection {
  edges: (OrderEdge & { node: IOrdersEdgesNode })[];
}

export interface IWishlistReference extends MetafieldReferenceConnection {
  edges: (MetafieldReferenceEdge & IWishlistNode)[];
}

export interface IWishlistNode extends MetafieldReferenceEdge {
  node: Product & {
    index: number;
    launch?: Metafield;
    variants: ProductVariantConnection;
  };
}

export interface ICustomerQuery {
  customer: Customer & {
    orders: IOrdersEdges;
    gender?: Metafield;
    birth?: Metafield;
    height?: Metafield;
    weight?: Metafield;
    wishlist?: Metafield & {
      references: (MetafieldReferenceConnection & {
        edges: IWishlistReference;
      })[];
    };
  };
}

export const customerQuery = query(
  {
    operation: "customer",
    fields: [
      ...customerFields,
      { defaultAddress: mailingAddressFields },
      addresses,
      orders,
      genderMetafield,
      birthMetafield,
      heightMetafield,
      weightMetafield,
      wishListMetafield,
    ],
    variables: { customerAccessToken: { type: "String!" } },
  },
  null,
  { operationName: "Customer" },
);

/**
 * 고객 정보 수정
 */
export interface ICustomerUpdateMutation {
  customerUpdate: CustomerUpdatePayload & {
    customer: ICustomerQuery["customer"];
  };
}

export const customerUpdateMutation = mutation(
  {
    operation: "customerUpdate",
    fields: [
      {
        customer: [
          ...customerFields,
          { defaultAddress: mailingAddressFields },
          addresses,
          orders,
          genderMetafield,
          birthMetafield,
          heightMetafield,
          weightMetafield,
          wishListMetafield,
        ],
      },
      { customerAccessToken: customerAccessTokenFields },
      { customerUserErrors: customerUserErrorFields },
      { userErrors: userErrorFields },
    ],
    variables: {
      customer: { type: "CustomerUpdateInput!" },
      customerAccessToken: { type: "String!" },
    },
  },
  undefined,
  { operationName: "CustomerUpdate" },
);

/**
 * 고객 주문 목록 조회
 */
export interface ICustomerOrdersQuery {
  customer: Customer & {
    email: string;
    orders: OrderConnection & {
      edges: (OrderEdge & { node: IOrdersEdgesNode })[];
      pageInfo: PageInfo;
    };
  };
}

export const customerOrdersQuery = query(
  {
    operation: "customer",
    fields: [
      {
        operation: "orders",
        fields: [
          {
            edges: [
              {
                node: [
                  ...orderFields,
                  lineItems,
                  successfulFulfillments,
                  drawStatusMetafield,
                  isOfflineMetafield,
                  featuredImageMetafield,
                  returnStatusMetafield,
                  instagramMetafield,
                  serialNumberMetafield,
                  scoreMetafield,
                  productCodeMetafield,
                  cardInfoMetafield,
                  excludeLimitMetafield,
                  discountApplications,
                  isFamilySaleMetafield,
                  ...orderMetafields[APP_NAME],
                ],
              },
            ],
          },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int" },
          sortKey: { type: "OrderSortKeys" },
          reverse: { type: "Boolean" },
          after: { type: "String" },
        },
      },
    ],
    variables: { customerAccessToken: { type: "String!" } },
  },
  null,
  { operationName: "CustomerOrder" },
);

export const totalPaidAmountQuery = query(
  {
    operation: "customer",
    fields: [
      "email",
      {
        operation: "orders",
        fields: [
          { edges: [{ node: [...orderAmountFields, isFamilySaleMetafield] }] },
          { pageInfo: pageFields },
        ],
        variables: {
          first: { type: "Int" },
          sortKey: { type: "OrderSortKeys" },
          reverse: { type: "Boolean" },
          after: { type: "String" },
          query: { type: "String" },
        },
      },
    ],
    variables: { customerAccessToken: { type: "String!" } },
  },
  null,
  { operationName: "TotalPaidAmount" },
);
